import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
export function CartDrawer() {
  const {
    items,
    subtotal,
    isOpen,
    closeCart,
    removeItem,
    updateQuantity
  } = useCart();
  const navigate = useNavigate();
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // Close on escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, closeCart]);
  const handleCheckout = () => {
    closeCart();
    navigate('/checkout');
  };
  return <AnimatePresence data-id="element-38">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-charcoal/70 backdrop-blur-sm z-[60]" onClick={closeCart} aria-hidden="true" data-id="element-39" />

          {/* Panel */}
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.3,
        ease: [0.2, 0.65, 0.3, 0.9]
      }} className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#F5F5F0] z-[70] shadow-2xl flex flex-col" role="dialog" aria-label="Shopping cart" data-id="element-40">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#D8D8D0] bg-charcoal text-white" data-id="element-41">
              <div className="flex items-center gap-3" data-id="element-42">
                <ShoppingBag className="w-5 h-5 text-tealslate" data-id="element-43" />
                <h2 className="font-serif text-xl" data-id="element-44">Your Cart</h2>
              </div>
              <button onClick={closeCart} className="p-2 hover:text-tealslate transition-colors" aria-label="Close cart" data-id="element-45">
                <X className="w-5 h-5" data-id="element-46" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-47">
              {items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center" data-id="element-48">
                  <ShoppingBag className="w-12 h-12 text-[#9A9A90] mb-5" data-id="element-49" />
                  <h3 className="text-xl font-serif text-[#1F1F1F] mb-3" data-id="element-50">
                    Your cart is empty.
                  </h3>
                  <p className="text-sm text-[#3B3B3B] mb-8 max-w-xs leading-relaxed" data-id="element-51">
                    Browse the bookshop or shop to add items.
                  </p>
                  <div className="flex flex-col gap-3 w-full max-w-xs" data-id="element-52">
                    <Link to="/books" onClick={closeCart} className="px-6 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-navyslate transition-colors inline-flex items-center justify-center gap-2" data-id="element-53">
                      Browse Books <ArrowRight className="w-3 h-3" data-id="element-54" />
                    </Link>
                    <Link to="/shop" onClick={closeCart} className="px-6 py-3 border border-[#1F1F1F] text-[#1F1F1F] text-xs font-bold uppercase tracking-wider hover:bg-[#1F1F1F] hover:text-white transition-colors inline-flex items-center justify-center gap-2" data-id="element-55">
                      Shop Supplements <ArrowRight className="w-3 h-3" data-id="element-56" />
                    </Link>
                  </div>
                </div> : <ul className="space-y-5" data-id="element-57">
                  {items.map(item => <li key={item.productId} className="bg-white border border-[#D8D8D0] p-5" data-id="element-58">
                      <div className="flex justify-between gap-4 mb-4" data-id="element-59">
                        <div className="min-w-0" data-id="element-60">
                          <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.2em] mb-1" data-id="element-61">
                            {item.type === 'book' ? 'Digital Book' : 'Supplement'}
                          </p>
                          <p className="font-serif text-base text-[#1F1F1F] leading-tight" data-id="element-62">
                            {item.name}
                          </p>
                        </div>
                        <button onClick={() => removeItem(item.productId)} className="text-[#9A9A90] hover:text-burgundy transition-colors shrink-0" aria-label={`Remove ${item.name}`} data-id="element-63">
                          <Trash2 className="w-4 h-4" data-id="element-64" />
                        </button>
                      </div>

                      <div className="flex items-center justify-between" data-id="element-65">
                        <div className="flex items-center border border-[#D8D8D0]" data-id="element-66">
                          <button onClick={() => updateQuantity(item.productId, item.quantity - 1)} className="px-3 py-2 text-[#3B3B3B] hover:bg-[#F5F5F0] transition-colors" aria-label="Decrease quantity" data-id="element-67">
                            <Minus className="w-3 h-3" data-id="element-68" />
                          </button>
                          <span className="px-4 text-sm font-bold text-[#1F1F1F] min-w-[2rem] text-center" data-id="element-69">
                            {item.quantity}
                          </span>
                          <button onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="px-3 py-2 text-[#3B3B3B] hover:bg-[#F5F5F0] transition-colors" aria-label="Increase quantity" data-id="element-70">
                            <Plus className="w-3 h-3" data-id="element-71" />
                          </button>
                        </div>
                        <p className="text-[#1F1F1F] font-bold" data-id="element-72">
                          {formatPrice(item.unitPrice * item.quantity)}
                        </p>
                      </div>
                    </li>)}
                </ul>}
            </div>

            {/* Footer */}
            {items.length > 0 && <div className="border-t border-[#D8D8D0] bg-white px-6 py-6 space-y-4" data-id="element-73">
                <div className="flex items-center justify-between" data-id="element-74">
                  <span className="text-[10px] font-bold text-[#7A7A70] uppercase tracking-[0.25em]" data-id="element-75">
                    Subtotal
                  </span>
                  <span className="text-2xl font-serif text-[#1F1F1F]" data-id="element-76">
                    {formatPrice(subtotal)}
                  </span>
                </div>
                <p className="text-xs text-[#7A7A70] italic" data-id="element-77">
                  Shipping and taxes calculated at checkout.
                </p>
                <button onClick={handleCheckout} className="w-full px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors inline-flex items-center justify-center gap-3" data-id="element-78">
                  Checkout <ArrowRight className="w-4 h-4" data-id="element-79" />
                </button>
              </div>}
          </motion.aside>
        </>}
    </AnimatePresence>;
}